import { projectLatLngToRoute } from 'features/Responder/features/Tours';
import { DESTINATION_PROXIMITY_METERS, isAtCheckpoint } from './checkpoints';
import { distanceMeters, resolveStopLatLng } from './geo';
import { formatDuration } from './formatters';
import { pickStopLabel, resolveTourTravelMode } from './tourResolvers';

export const buildTelemetryData = ({
  schedule,
  tour,
  tourStops,
  checkpointCount,
  latestPersonnelLocation,
  recentPersonnelLocations,
  routePoints,
  routeMetrics,
  totalDurationSeconds,
  origin,
  destination,
}) => {
  const travelMode = resolveTourTravelMode(tour, tourStops).label;
  const base = {
    status: 'Unknown',
    nextCheckpoint: null,
    lastCheckpoint: null,
    etaToNextCheckpoint: 'Not computed',
    etaToDestination: 'Not computed',
    travelMode,
    checkpointsPassed: 0,
    checkpointsTotal: checkpointCount || 0,
    nextCheckpointCenter: null,
    destinationCenter: null,
    position: null,
  };

  if (!schedule || schedule.entity.type !== 'Tour') return base;

  const location = latestPersonnelLocation;
  if (!location) {
    return { ...base, status: 'No live location' };
  }

  const position = { lat: location.lat, lng: location.lng };
  const recentUpdates = recentPersonnelLocations.slice(0, 2);
  const speedMetersPerSecond = (() => {
    if (recentUpdates.length < 2) return null;
    const [latest, prev] = recentUpdates;
    const latestTime = latest.timestampMs;
    const prevTime = prev.timestampMs;
    if (!Number.isFinite(latestTime) || !Number.isFinite(prevTime)) return null;
    const dtSeconds = (latestTime - prevTime) / 1000;
    if (!Number.isFinite(dtSeconds) || dtSeconds <= 1) return null;
    const dist = distanceMeters(latest, prev);
    if (!Number.isFinite(dist) || dist <= 0) return null;
    const speed = dist / dtSeconds;
    return speed >= 0.4 ? speed : null;
  })();

  const hasRoute = routePoints.length > 1 && routeMetrics.total > 0;
  let currentAlong = 0;
  if (hasRoute) {
    const proj = projectLatLngToRoute(routePoints, routeMetrics, position);
    currentAlong = Number.isFinite(proj.along) ? proj.along : 0;
  }

  const rawNodes = tourStops;
  let checkpointNodes = rawNodes.filter((node) => node?.type === 'checkpoint');
  if (!checkpointNodes.length && rawNodes.length > 2) {
    checkpointNodes = rawNodes.slice(1, rawNodes.length - 1);
  }

  const checkpointEntries = checkpointNodes
    .map((node, index) => {
      const center = resolveStopLatLng(node);
      if (!center) return null;
      const along = hasRoute
        ? projectLatLngToRoute(routePoints, routeMetrics, center).along
        : null;
      const key = node?.id ?? node?.checkpointId ?? node?.stopId ?? `idx-${index}`;
      return {
        node,
        center,
        along,
        key,
        label: pickStopLabel(node, `Checkpoint ${index + 1}`),
      };
    })
    .filter(Boolean);

  const checkpointHit = checkpointEntries.find((entry) => isAtCheckpoint(position, entry.node));
  const recentTrail = recentPersonnelLocations.slice(0, 200);
  let lastHitIndex = -1;
  if (recentTrail.length && checkpointEntries.length) {
    const ordered = checkpointEntries
      .filter((entry) => Number.isFinite(entry.along))
      .sort((a, b) => a.along - b.along);
    for (const loc of recentTrail) {
      const hitIdx = ordered.findIndex((entry) =>
        isAtCheckpoint({ lat: loc.lat, lng: loc.lng }, entry.node),
      );
      if (hitIdx >= 0) {
        lastHitIndex = hitIdx;
        break;
      }
    }
    if (lastHitIndex === -1 && checkpointHit) {
      const hitKey = checkpointHit.key;
      lastHitIndex = ordered.findIndex((entry) => entry.key === hitKey);
    }
  }

  let nextCheckpoint = null;
  let lastCheckpoint = null;
  const isLooped = tour?.isLooped === true;

  if (checkpointEntries.length) {
    if (hasRoute) {
      const ordered = checkpointEntries
        .filter((entry) => Number.isFinite(entry.along))
        .sort((a, b) => a.along - b.along);
      if (ordered.length) {
        if (lastHitIndex >= 0) {
          const nextIdx = isLooped ? (lastHitIndex + 1) % ordered.length : lastHitIndex + 1;
          nextCheckpoint = ordered[nextIdx] || ordered[ordered.length - 1];
          lastCheckpoint = ordered[lastHitIndex] || null;
        } else {
          nextCheckpoint =
            ordered.find((entry) => entry.along >= currentAlong - 1) ||
            ordered[ordered.length - 1];
          lastCheckpoint =
            ordered.filter((entry) => entry.along < currentAlong - 1).pop() || null;
        }
      }
    } else {
      const nearest = checkpointEntries.reduce((best, entry) => {
        const dist = distanceMeters(position, entry.center);
        if (!best || dist < best.dist) return { ...entry, dist };
        return best;
      }, null);
      if (nearest) {
        const { dist: _omit, ...rest } = nearest;
        nextCheckpoint = rest;
      }
    }
  }

  if (checkpointHit) {
    lastCheckpoint = checkpointHit;
  }

  const checkpointsTotal = checkpointEntries.length || checkpointCount || 0;
  let checkpointsPassed =
    lastHitIndex >= 0
      ? lastHitIndex + 1
      : hasRoute && checkpointEntries.length
        ? checkpointEntries.filter(
            (entry) => Number.isFinite(entry.along) && entry.along < currentAlong - 1,
          ).length
        : 0;

  const isAtOrigin = !isLooped && origin && isAtCheckpoint(position, origin);
  if (isAtOrigin) {
    checkpointsPassed = 0;
  }

  const distanceToNextCheckpoint = nextCheckpoint?.center
    ? distanceMeters(position, nextCheckpoint.center)
    : null;

  const etaToNextCheckpointSeconds =
    Number.isFinite(distanceToNextCheckpoint) && speedMetersPerSecond
      ? distanceToNextCheckpoint / speedMetersPerSecond
      : hasRoute &&
        nextCheckpoint &&
        Number.isFinite(nextCheckpoint.along) &&
        Number.isFinite(totalDurationSeconds) &&
        routeMetrics.total > 0
        ? (totalDurationSeconds * Math.max(0, nextCheckpoint.along - currentAlong)) /
          routeMetrics.total
        : null;

  const etaToDestinationSeconds =
    hasRoute && Number.isFinite(totalDurationSeconds) && routeMetrics.total > 0
      ? (totalDurationSeconds * Math.max(0, routeMetrics.total - currentAlong)) /
        routeMetrics.total
      : null;

  let status = 'Traveling';
  const destinationCenter = resolveStopLatLng(destination);
  if (
    destinationCenter &&
    distanceMeters(position, destinationCenter) <= DESTINATION_PROXIMITY_METERS
  ) {
    status = 'Finished';
  } else if (checkpointHit) {
    status = 'In Checkpoint';
  }

  return {
    status,
    nextCheckpoint: nextCheckpoint?.label || null,
    lastCheckpoint: lastCheckpoint?.label || null,
    etaToNextCheckpoint: formatDuration(etaToNextCheckpointSeconds),
    etaToDestination: formatDuration(etaToDestinationSeconds),
    travelMode,
    checkpointsPassed,
    checkpointsTotal,
    nextCheckpointCenter: nextCheckpoint?.center || null,
    destinationCenter: destinationCenter || null,
    position,
  };
};


